import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import Logo from '../Logo';
import * as styles from './Auth.module.css';

const ResetPasswordConfirm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle the auth callback
    const handleAuthCallback = async () => {
      try {
        // Check if we have the required URL parameters
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const type = searchParams.get('type');

        if (type === 'recovery' && accessToken && refreshToken) {
          // Set the session with the tokens from the URL
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting session:', error);
            setError('Invalid or expired reset link');
            return;
          }

          console.log('Session set successfully for password reset');
        } else {
          // If no proper parameters, check if we already have a session
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            setError('Invalid or expired reset link');
            setTimeout(() => navigate('/login'), 3000);
          }
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        setError('Something went wrong. Please try again.');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError("Passwords don't match");
    }

    if (password.length < 6) {
      return setError("Password should be at least 6 characters");
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;
      
      setMessage('Password updated successfully! Redirecting to login...');
      
      // Sign out and redirect to login
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authFormContainer}>
        <div className={styles.authHeader}>
          <Logo />
          <h2>Set New Password</h2>
          <p>Enter your new password below</p>
        </div>
        
        {error && !password ? (
          <div className={styles.authError}>
            <p>{error}</p>
            <button onClick={() => navigate('/password-reset')} className={styles.authButton}>
              Request New Reset Link
            </button>
          </div>
        ) : (
          <form onSubmit={handlePasswordUpdate} className={styles.authForm}>
            <div className={styles.formGroup}>
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="New password"
                minLength={6}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                minLength={6}
              />
            </div>
            
            {error && <div className={styles.authError}>{error}</div>}
            {message && <div className={styles.authSuccess}>{message}</div>}
            
            <button 
              type="submit" 
              className={styles.authButton}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;